import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, SlidersHorizontal, Zap, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import type { FilterState, ItemCategory, Phase, Quality, Rarity, WeaponType } from '@/types';

// Цвета редкостей из CS2
const rarityColors: Record<string, string> = {
  'Consumer Grade':   '#b0c3d9',
  'Industrial Grade': '#5e98d9',
  'Mil-Spec Grade':   '#4b69ff',
  'Restricted':       '#8847ff',
  'Classified':       '#d32ce6',
  'Covert':           '#eb4b4b',
  'Contraband':       '#ffae00',
  'Extraordinary':    '#ffd700',
};

// Локализованные названия редкостей (внутренние значения не меняются — они уходят в API)
const rarityLabels: Record<Rarity, string> = {
  'Consumer Grade':   'Ширпотреб',
  'Industrial Grade': 'Промышленное',
  'Mil-Spec Grade':   'Армейское',
  'Restricted':       'Запрещённое',
  'Classified':       'Засекреченное',
  'Covert':           'Тайное',
  'Contraband':       'Контрабандное',
  'Extraordinary':    'Экстраординарное',
};

// Локализованные названия типов оружия
const weaponTypeLabels: Record<WeaponType, string> = {
  'Pistols':     'Пистолеты',
  'Rifles':      'Винтовки',
  'SMGs':        'Пистолет-пулемёты',
  'Heavy':       'Тяжёлое',
  'Knives':      'Ножи',
  'Gloves':      'Перчатки',
  'Container':   'Контейнеры',
  'Sticker':     'Стикеры',
  'Agent':       'Агенты',
  'Music Kit':   'Муз. наборы',
  'Collectible': 'Коллекционное',
  'Patch':       'Нашивки',
  'Graffiti':    'Граффити',
  'Equipment':   'Снаряжение',
};

const PRICE_MAX = 100_000; // максимум слайдера (₽)

interface FiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
}

const rarities: Rarity[] = [
  'Consumer Grade', 'Industrial Grade', 'Mil-Spec Grade', 'Restricted',
  'Classified', 'Covert', 'Contraband', 'Extraordinary',
];

const weaponTypes: WeaponType[] = [
  'Pistols', 'Rifles', 'SMGs', 'Heavy', 'Knives', 'Gloves',
  'Container', 'Sticker', 'Agent', 'Music Kit', 'Collectible',
  'Patch', 'Graffiti', 'Equipment',
];

// Категории экземпляра. Значение уезжает на бэк как есть (см. web_routes:
// параметр `categories`), подпись — только для UI.
const itemCategories: { value: ItemCategory; label: string }[] = [
  { value: 'normal',   label: 'Обычное' },
  { value: 'stattrak', label: 'StatTrak™' },
  { value: 'souvenir', label: 'Сувенир' },
  { value: 'star',     label: '★' },
];

// Фазы Doppler. Приходят из расширенного прайса пер-офферно.
const phases: Phase[] = [
  'Phase 1', 'Phase 2', 'Phase 3', 'Phase 4',
  'Emerald', 'Sapphire', 'Ruby', 'Black Pearl',
];

// Границы износа CS2. Кнопки задают диапазон float одним нажатием —
// это то же самое, что выставить слайдер руками.
const WEAR_PRESETS: { code: string; label: Quality; from: number; to: number }[] = [
  { code: 'FN', label: 'Factory New',    from: 0,    to: 0.07 },
  { code: 'MW', label: 'Minimal Wear',   from: 0.07, to: 0.15 },
  { code: 'FT', label: 'Field-Tested',   from: 0.15, to: 0.38 },
  { code: 'WW', label: 'Well-Worn',      from: 0.38, to: 0.45 },
  { code: 'BS', label: 'Battle-Scarred', from: 0.45, to: 1 },
];

export function Filters({ filters, onFiltersChange }: FiltersProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    category: true,
    price: true,
    delivery: true,
    wear: true,
    rarity: true,
    weapon: true,
    phase: false,
  });

  // Локальное состояние слайдера — обновляется во время перетаскивания без
  // лишних API-запросов; фильтр применяется только после отпускания (onValueCommit).
  const [sliderValues, setSliderValues] = useState<[number, number]>([
    filters.priceFrom ?? 0,
    filters.priceTo ?? PRICE_MAX,
  ]);

  // Синхронизация слайдера при внешнем сбросе фильтров
  useEffect(() => {
    setSliderValues([filters.priceFrom ?? 0, filters.priceTo ?? PRICE_MAX]);
  }, [filters.priceFrom, filters.priceTo]);

  // Float 0..1 — тот же паттерн, что у цены: тянем локально, применяем на отпускании.
  const [floatValues, setFloatValues] = useState<[number, number]>([
    filters.floatFrom ?? 0,
    filters.floatTo ?? 1,
  ]);

  useEffect(() => {
    setFloatValues([filters.floatFrom ?? 0, filters.floatTo ?? 1]);
  }, [filters.floatFrom, filters.floatTo]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleRarity = (rarity: Rarity) => {
    const newRarities = filters.rarities.includes(rarity)
      ? filters.rarities.filter(r => r !== rarity)
      : [...filters.rarities, rarity];
    onFiltersChange({ ...filters, rarities: newRarities });
  };

  const toggleWeapon = (weapon: WeaponType) => {
    const newWeapons = filters.weapons.includes(weapon)
      ? filters.weapons.filter(w => w !== weapon)
      : [...filters.weapons, weapon];
    onFiltersChange({ ...filters, weapons: newWeapons });
  };

  const toggleCategory = (c: ItemCategory) => {
    const next = filters.categories.includes(c)
      ? filters.categories.filter(x => x !== c)
      : [...filters.categories, c];
    onFiltersChange({ ...filters, categories: next });
  };

  const togglePhase = (p: Phase) => {
    const next = filters.phases.includes(p)
      ? filters.phases.filter(x => x !== p)
      : [...filters.phases, p];
    onFiltersChange({ ...filters, phases: next });
  };

  /** Кнопка износа активна, когда её диапазон совпадает с текущим float. */
  const isWearActive = (from: number, to: number) =>
    filters.floatFrom === from && filters.floatTo === to;

  const applyWearPreset = (from: number, to: number) => {
    if (isWearActive(from, to)) {
      onFiltersChange({ ...filters, floatFrom: undefined, floatTo: undefined });
      setFloatValues([0, 1]);
    } else {
      onFiltersChange({ ...filters, floatFrom: from, floatTo: to });
      setFloatValues([from, to]);
    }
  };

  const activeFiltersCount =
    filters.rarities.length +
    filters.weapons.length +
    filters.categories.length +
    filters.phases.length +
    (filters.delivery ? 1 : 0) +
    (filters.hasSticker ? 1 : 0) +
    (filters.hasKeychain ? 1 : 0) +
    (filters.floatFrom !== undefined || filters.floatTo !== undefined ? 1 : 0) +
    (filters.priceFrom ? 1 : 0) +
    (filters.priceTo ? 1 : 0);

  return (
    <div className="w-full lg:w-64 bg-[#161b22] rounded-xl border border-[#30363d] p-4 space-y-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto scrollbar-dark">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-[#ff6b00]" />
          <span className="text-white font-semibold">Фильтры</span>
        </div>
        {activeFiltersCount > 0 && (
          <Badge className="bg-[#ff6b00] text-white">{activeFiltersCount}</Badge>
        )}
      </div>

      {/* StatTrak Toggle */}
      <div className="flex items-center justify-between py-2 border-b border-[#30363d]">
        <span className="text-[#8b949e] text-sm">Только StatTrak</span>
        <Checkbox
          checked={filters.statTrakOnly}
          onCheckedChange={(checked) =>
            onFiltersChange({ ...filters, statTrakOnly: checked as boolean })
          }
          className="border-[#30363d] data-[state=checked]:bg-[#ff6b00] data-[state=checked]:border-[#ff6b00]"
        />
      </div>

      {/* Категория экземпляра */}
      <div className="space-y-2">
        <button
          onClick={() => toggleSection('category')}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="text-white font-medium text-sm">Категория</span>
          {expandedSections.category
            ? <ChevronUp className="w-4 h-4 text-[#8b949e]" />
            : <ChevronDown className="w-4 h-4 text-[#8b949e]" />
          }
        </button>

        {expandedSections.category && (
          <div className="space-y-2 pt-1">
            {itemCategories.map((c) => (
              <label key={c.value} className="flex items-center gap-2 cursor-pointer group">
                <Checkbox
                  checked={filters.categories.includes(c.value)}
                  onCheckedChange={() => toggleCategory(c.value)}
                  className="border-[#30363d] data-[state=checked]:bg-[#ff6b00] data-[state=checked]:border-[#ff6b00]"
                />
                <span className="text-[#8b949e] text-sm group-hover:text-white transition-colors">
                  {c.label}
                </span>
              </label>
            ))}

            {/* Наличие аксессуаров — та же секция по смыслу («что на предмете») */}
            <label className="flex items-center gap-2 cursor-pointer group pt-1">
              <Checkbox
                checked={filters.hasKeychain}
                onCheckedChange={(v) =>
                  onFiltersChange({ ...filters, hasKeychain: v as boolean })
                }
                className="border-[#30363d] data-[state=checked]:bg-[#ff6b00] data-[state=checked]:border-[#ff6b00]"
              />
              <span className="text-[#8b949e] text-sm group-hover:text-white transition-colors">
                Наличие брелка
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer group">
              <Checkbox
                checked={filters.hasSticker}
                onCheckedChange={(v) =>
                  onFiltersChange({ ...filters, hasSticker: v as boolean })
                }
                className="border-[#30363d] data-[state=checked]:bg-[#ff6b00] data-[state=checked]:border-[#ff6b00]"
              />
              <span className="text-[#8b949e] text-sm group-hover:text-white transition-colors">
                Наличие наклейки
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Price Range */}
      <div className="space-y-2">
        <button
          onClick={() => toggleSection('price')}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="text-white font-medium text-sm">Цена</span>
          {expandedSections.price
            ? <ChevronUp className="w-4 h-4 text-[#8b949e]" />
            : <ChevronDown className="w-4 h-4 text-[#8b949e]" />
          }
        </button>

        {expandedSections.price && (
          <div className="space-y-3 pt-1">
            {/* Текущие значения */}
            <div className="flex justify-between text-xs text-[#8b949e]">
              <span>{sliderValues[0].toLocaleString('ru-RU')} ₽</span>
              <span>
                {sliderValues[1] >= PRICE_MAX
                  ? `${PRICE_MAX.toLocaleString('ru-RU')}+ ₽`
                  : `${sliderValues[1].toLocaleString('ru-RU')} ₽`}
              </span>
            </div>

            {/* Range slider */}
            <Slider
              min={0}
              max={PRICE_MAX}
              step={100}
              value={sliderValues}
              onValueChange={(vals) => setSliderValues([vals[0], vals[1]])}
              onValueCommit={(vals) => {
                onFiltersChange({
                  ...filters,
                  priceFrom: vals[0] > 0 ? vals[0] : undefined,
                  priceTo: vals[1] < PRICE_MAX ? vals[1] : undefined,
                });
              }}
              className="[&_[data-slot=slider-range]]:bg-[#ff6b00] [&_[data-slot=slider-thumb]]:border-[#ff6b00] [&_[data-slot=slider-thumb]]:hover:ring-[#ff6b00]/30 [&_[data-slot=slider-track]]:bg-[#30363d]"
            />

            {/* Точный ввод */}
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="От"
                value={filters.priceFrom || ''}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : undefined;
                  onFiltersChange({ ...filters, priceFrom: val });
                }}
                className="bg-[#0d1117] border-[#30363d] text-white placeholder:text-[#8b949e] focus:border-[#ff6b00] text-sm"
              />
              <Input
                type="number"
                placeholder="До"
                value={filters.priceTo || ''}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : undefined;
                  onFiltersChange({ ...filters, priceTo: val });
                }}
                className="bg-[#0d1117] border-[#30363d] text-white placeholder:text-[#8b949e] focus:border-[#ff6b00] text-sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* Доставка. delivery=1 у Xpanda — ручная выдача (до 12 часов),
          null или 2 — авто (~минута). */}
      <div className="space-y-2">
        <button
          onClick={() => toggleSection('delivery')}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="text-white font-medium text-sm">Доставка</span>
          {expandedSections.delivery
            ? <ChevronUp className="w-4 h-4 text-[#8b949e]" />
            : <ChevronDown className="w-4 h-4 text-[#8b949e]" />
          }
        </button>

        {expandedSections.delivery && (
          <div className="grid grid-cols-3 gap-1 pt-1">
            {([
              ['',     'Любая',    null,  ''],
              ['fast', 'Быстрая',  Zap,   'text-green-400'],
              ['slow', 'До 12 ч',  Clock, 'text-amber-400'],
            ] as const).map(([value, label, Icon, iconClass]) => (
              <button
                key={value || 'any'}
                onClick={() => onFiltersChange({ ...filters, delivery: value })}
                className={`px-1 py-1.5 rounded text-xs border transition-colors
                  flex flex-col items-center gap-0.5 ${
                  filters.delivery === value
                    ? 'bg-[#ff6b00]/20 border-[#ff6b00] text-white'
                    : 'bg-[#0d1117] border-[#30363d] text-[#8b949e] hover:border-[#8b949e]'
                }`}
              >
                {Icon && <Icon className={`w-3.5 h-3.5 ${iconClass}`} />}
                <span className="leading-tight">{label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Float */}
      <div className="space-y-2">
        <button
          onClick={() => toggleSection('wear')}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="text-white font-medium text-sm">Float</span>
          {expandedSections.wear
            ? <ChevronUp className="w-4 h-4 text-[#8b949e]" />
            : <ChevronDown className="w-4 h-4 text-[#8b949e]" />
          }
        </button>

        {expandedSections.wear && (
          <div className="space-y-3 pt-1">
            <div className="flex justify-between text-xs text-[#8b949e]">
              <span>{floatValues[0].toFixed(2)}</span>
              <span>{floatValues[1].toFixed(2)}</span>
            </div>

            {/* Трек раскрашен по границам износа CS2 — те же цвета, что у
                шкалы Float в карточке товара, чтобы позиция читалась одинаково
                в обоих местах. Выбранный диапазон оставляем прозрачным, иначе
                оранжевая заливка перекрыла бы градиент. */}
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={floatValues}
              onValueChange={(vals) => setFloatValues([vals[0], vals[1]])}
              onValueCommit={(vals) => {
                onFiltersChange({
                  ...filters,
                  floatFrom: vals[0] > 0 ? vals[0] : undefined,
                  floatTo: vals[1] < 1 ? vals[1] : undefined,
                });
              }}
              style={{
                ['--float-track' as string]:
                  'linear-gradient(to right, #4b69ff 0%, #4b69ff 7%, #4cd964 7%,' +
                  ' #4cd964 15%, #ffd700 15%, #ffd700 38%, #ff8c00 38%,' +
                  ' #ff8c00 45%, #eb4b4b 45%, #eb4b4b 100%)',
              }}
              className="[&_[data-slot=slider-track]]:bg-[image:var(--float-track)]
                         [&_[data-slot=slider-range]]:bg-transparent
                         [&_[data-slot=slider-thumb]]:border-white
                         [&_[data-slot=slider-thumb]]:bg-white
                         [&_[data-slot=slider-thumb]]:hover:ring-white/30"
            />

            <div className="flex gap-2">
              <Input
                type="number" step="0.01" min={0} max={1}
                placeholder="Minimum"
                value={filters.floatFrom ?? ''}
                onChange={(e) => onFiltersChange({
                  ...filters,
                  floatFrom: e.target.value ? Number(e.target.value) : undefined,
                })}
                className="bg-[#0d1117] border-[#30363d] text-white placeholder:text-[#8b949e] focus:border-[#ff6b00] text-sm"
              />
              <Input
                type="number" step="0.01" min={0} max={1}
                placeholder="Maximum"
                value={filters.floatTo ?? ''}
                onChange={(e) => onFiltersChange({
                  ...filters,
                  floatTo: e.target.value ? Number(e.target.value) : undefined,
                })}
                className="bg-[#0d1117] border-[#30363d] text-white placeholder:text-[#8b949e] focus:border-[#ff6b00] text-sm"
              />
            </div>

            {/* Пресеты износа — сокращения из CS2 */}
            <div className="grid grid-cols-5 gap-1">
              {WEAR_PRESETS.map((w) => (
                <button
                  key={w.code}
                  title={w.label}
                  onClick={() => applyWearPreset(w.from, w.to)}
                  className={`text-xs py-1.5 rounded border transition-colors ${
                    isWearActive(w.from, w.to)
                      ? 'bg-[#ff6b00] border-[#ff6b00] text-white'
                      : 'bg-[#0d1117] border-[#30363d] text-[#8b949e] hover:border-[#8b949e]'
                  }`}
                >
                  {w.code}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Фаза (Doppler) */}
      <div className="space-y-2">
        <button
          onClick={() => toggleSection('phase')}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="text-white font-medium text-sm">Фаза</span>
          {expandedSections.phase
            ? <ChevronUp className="w-4 h-4 text-[#8b949e]" />
            : <ChevronDown className="w-4 h-4 text-[#8b949e]" />
          }
        </button>

        {expandedSections.phase && (
          <div className="space-y-2 pt-1">
            {phases.map((p) => (
              <label key={p} className="flex items-center gap-2 cursor-pointer group">
                <Checkbox
                  checked={filters.phases.includes(p)}
                  onCheckedChange={() => togglePhase(p)}
                  className="border-[#30363d] data-[state=checked]:bg-[#ff6b00] data-[state=checked]:border-[#ff6b00]"
                />
                <span className="text-[#8b949e] text-sm group-hover:text-white transition-colors">
                  {p}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Rarity */}
      <div className="space-y-2">
        <button
          onClick={() => toggleSection('rarity')}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="text-white font-medium text-sm">Редкость</span>
          {expandedSections.rarity
            ? <ChevronUp className="w-4 h-4 text-[#8b949e]" />
            : <ChevronDown className="w-4 h-4 text-[#8b949e]" />
          }
        </button>
        {expandedSections.rarity && (
          <div className="space-y-1.5">
            {rarities.map((rarity) => (
              <label
                key={rarity}
                className="flex items-center gap-2 cursor-pointer hover:bg-[#0d1117] p-1.5 rounded transition-colors"
              >
                <Checkbox
                  checked={filters.rarities.includes(rarity)}
                  onCheckedChange={() => toggleRarity(rarity)}
                  className="border-[#30363d] data-[state=checked]:bg-[#ff6b00] data-[state=checked]:border-[#ff6b00]"
                />
                <span className="text-[#8b949e] text-sm">{rarityLabels[rarity]}</span>
                <div
                  className="w-3 h-3 rounded-full ml-auto flex-shrink-0"
                  style={{ backgroundColor: rarityColors[rarity] }}
                />
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Weapon Type */}
      <div className="space-y-2">
        <button
          onClick={() => toggleSection('weapon')}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="text-white font-medium text-sm">Тип оружия</span>
          {expandedSections.weapon
            ? <ChevronUp className="w-4 h-4 text-[#8b949e]" />
            : <ChevronDown className="w-4 h-4 text-[#8b949e]" />
          }
        </button>
        {expandedSections.weapon && (
          <div className="space-y-1.5">
            {weaponTypes.map((weapon) => (
              <label
                key={weapon}
                className="flex items-center gap-2 cursor-pointer hover:bg-[#0d1117] p-1.5 rounded transition-colors"
              >
                <Checkbox
                  checked={filters.weapons.includes(weapon)}
                  onCheckedChange={() => toggleWeapon(weapon)}
                  className="border-[#30363d] data-[state=checked]:bg-[#ff6b00] data-[state=checked]:border-[#ff6b00]"
                />
                <span className="text-[#8b949e] text-sm">{weaponTypeLabels[weapon]}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Reset Button */}
      {activeFiltersCount > 0 && (
        <Button
          variant="outline"
          onClick={() => {
            onFiltersChange({
              qualities: [],
              rarities: [],
              weapons: [],
              search: '',
              statTrakOnly: false,
              priceFrom: undefined,
              priceTo: undefined,
              categories: [],
              phases: [],
              hasSticker: false,
              delivery: '',
              hasKeychain: false,
              floatFrom: undefined,
              floatTo: undefined,
            });
          }}
          className="w-full border-[#30363d] text-[#8b949e] hover:text-white hover:bg-[#1a2332]"
        >
          Сбросить фильтры
        </Button>
      )}
    </div>
  );
}
